import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play, Pause } from "lucide-react";
import { FalajBackdrop } from "@/pages/sections/FalajBackdrop";

/**
 * Hero — permanent three-layer architecture.
 *
 *   1. Background layer  — /assets/hero-poster.webp (with .jpg fallback), or
 *                          the deep-green brand gradient if neither exists.
 *   2. Falaj layer       — code-rendered animated SVG (transparent, screen-
 *                          blended over the image so its green lines glow).
 *   3. Legibility overlay — as before.
 *   4. Content            — headline, sub, buttons, pause toggle.
 *
 * Video is OPTIONAL and REPLACES ONLY LAYER 1. It never replaces the Falaj.
 *
 * Video load is gated on:
 *   - REACT_APP_HERO_VIDEO === "1"
 *   - Files exist (browser reports canplay)
 *   - Viewport >= 768px, OR (viewport < 768px AND effectiveType === '4g' AND !saveData)
 *   - Deferred until after first paint via requestIdleCallback
 * The poster is what LCP measures — the video never blocks it.
 */
const HERO_VIDEO_ENABLED = process.env.REACT_APP_HERO_VIDEO === "1";

function shouldLoadVideoNow() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
  const isDesktop = window.matchMedia("(min-width: 768px)").matches;
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (isDesktop) {
    // On desktop we still respect Save-Data — a laptop tethered to a slow
    // phone should not download the video either.
    if (conn && conn.saveData) return false;
    return true;
  }
  // Mobile: only if the browser reports 4g and Save-Data is off.
  if (!conn) return false;
  if (conn.saveData) return false;
  return conn.effectiveType === "4g";
}

function pickVideoSet() {
  // Serve 720p to viewports under 1024px, 1080p above.
  if (typeof window === "undefined") return { size: "1080" };
  return window.matchMedia("(min-width: 1024px)").matches
    ? { size: "1080" }
    : { size: "720" };
}

export function Hero() {
  const videoRef = useRef(null);
  const [posterFailed, setPosterFailed] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [playing, setPlaying] = useState(true);

  const videoSet = useMemo(() => pickVideoSet(), []);

  // Decide whether to fetch the video, deferred until after first paint.
  useEffect(() => {
    if (!HERO_VIDEO_ENABLED) return;
    if (!shouldLoadVideoNow()) return;
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 300));
    const handle = idle(() => setShouldLoad(true));
    return () => {
      if (window.cancelIdleCallback && typeof handle === "number") window.cancelIdleCallback(handle);
    };
  }, []);

  // The pause/play button controls whichever motion layer is on top. If the
  // video is present it drives the video; otherwise it drives the Falaj.
  const togglePlay = () => {
    if (HERO_VIDEO_ENABLED && shouldLoad) {
      const v = videoRef.current;
      if (v) {
        if (v.paused) v.play().then(() => setPlaying(true)).catch(() => {});
        else { v.pause(); setPlaying(false); }
        return;
      }
    }
    setPlaying((p) => !p);
  };

  const size = videoSet.size; // "720" or "1080"

  return (
    <section
      data-testid="hero-section"
      className="relative isolate overflow-hidden bg-wafa-deep"
    >
      {/* --- Layer 1: background image (or brand gradient fallback) --------- */}
      {!posterFailed ? (
        <picture aria-hidden="true">
          <source srcSet="/assets/hero-poster.webp" type="image/webp" />
          <img
            src="/assets/hero-poster.jpg"
            alt=""
            data-testid="hero-poster"
            onError={() => setPosterFailed(true)}
            fetchPriority="high"
            className="absolute inset-0 -z-20 h-full w-full object-cover"
          />
        </picture>
      ) : null}
      {/* Brand gradient sits underneath as the universal fallback. If the
          poster loads it covers this; if it doesn't, this is the hero. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-30"
        style={{ background: "linear-gradient(135deg, #0E4F3A 0%, #19532B 55%, #0E4F3A 100%)" }}
      />

      {/* --- Layer 1 (variant): video, replaces the image when flag is on --- */}
      {HERO_VIDEO_ENABLED && shouldLoad ? (
        <video
          ref={videoRef}
          data-testid="hero-video"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="/assets/hero-poster.jpg"
          aria-hidden="true"
          onCanPlay={() => setVideoReady(true)}
          onError={() => setVideoReady(false)}
          className={`absolute inset-0 -z-20 h-full w-full object-cover transition-opacity duration-700 motion-reduce:hidden ${
            videoReady ? "opacity-100" : "opacity-0"
          }`}
        >
          {/* Explicit size-tiered sources. Browser picks the first it can play. */}
          <source src={`/assets/hero-loop-${size}.webm`} type="video/webm" />
          <source src={`/assets/hero-loop-${size}.mp4`} type="video/mp4" />
          {/* Fallback to the other size in case only one was generated. Only
              emit it when size==="720" so desktop doesn't list duplicate
              1080 <source>s that can never be picked. */}
          {size === "720" ? (
            <>
              <source src="/assets/hero-loop-1080.webm" type="video/webm" />
              <source src="/assets/hero-loop-1080.mp4" type="video/mp4" />
            </>
          ) : null}
        </video>
      ) : null}

      {/* --- Layer 2: Falaj — glows OVER whatever layer 1 is showing -------- */}
      <div className="absolute inset-0 -z-10 opacity-60 md:opacity-70">
        <FalajBackdrop paused={!playing} />
      </div>

      {/* --- Layer 3: legibility overlays --------------------------------- */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-[5]"
        style={{
          // Slightly darker at the left (0.88 → 0.92) so the eyebrow pill
          // and sub-headline stay legible over the brightest video frames.
          background:
            "linear-gradient(90deg, rgba(14,79,58,0.92) 0%, rgba(14,79,58,0.65) 45%, rgba(14,79,58,0.30) 100%)",
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 -z-[4] h-32"
        style={{ background: "linear-gradient(to bottom, rgba(14,79,58,0) 0%, rgba(14,79,58,0.6) 100%)" }}
      />

      {/* --- Layer 4: content -------------------------------------------- */}
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-24 lg:grid-cols-12 lg:gap-16 lg:px-6 lg:py-36">
        <div className="lg:col-span-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-wafa-sunshine" />
            <span>Oman-based software engineering · Technology partner to Altalib Group</span>
          </div>

          <h1
            data-testid="hero-heading"
            className="mt-6 max-w-4xl font-heading text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-7xl"
          >
            Software that runs your business,
            <span className="block text-wafa-sunshine">not the other way round.</span>
          </h1>

          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/90 lg:text-lg">
            We design, build and secure custom platforms, SaaS products and mobile apps — then we
            stay and run them. An Oman-based company, with engineers in four countries.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 rounded-md bg-wafa-primary px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-black/10 transition-colors duration-200 hover:bg-white hover:text-wafa-deep"
            >
              Book a Free Scoping Call
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/work"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 rounded-md border border-white/40 px-5 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:border-white hover:bg-white/10"
            >
              See our work
            </Link>
          </div>

          <div
            className="mt-12 grid gap-8 border-t border-white/20 pt-8 sm:mt-14 sm:pt-10 sm:grid-cols-3 sm:gap-3"
            data-testid="hero-stats-strip"
          >
            {[
              {
                testId: "hero-stat-countries",
                numeral: "4",
                line1: "Countries our team works from",
                line2: "Oman · India · USA · Philippines",
              },
              {
                testId: "hero-stat-support",
                numeral: "90",
                line1: "Days of free support",
                line2: "after we hand over",
              },
              {
                testId: "hero-stat-vision",
                numeral: "2040",
                line1: "We build for",
                line2: "Oman Vision 2040",
              },
            ].map((s, i) => (
              <div
                key={s.testId}
                data-testid={s.testId}
                className={`flex items-center gap-4 sm:gap-4 ${
                  // Vertical divider between the desktop columns — full height
                  // of the numeral only, very low opacity so it never fights
                  // the numeral for attention.
                  i > 0 ? "sm:border-s sm:border-white/15 sm:ps-4 lg:ps-6" : ""
                }`}
              >
                <div className="font-heading text-6xl font-bold leading-none tracking-tight text-white sm:text-4xl lg:text-6xl">
                  {s.numeral}
                </div>
                <div className="min-w-0 whitespace-nowrap text-sm leading-snug text-white/70 sm:text-[10px] lg:text-xs xl:text-sm">
                  {s.line1}
                  <br />
                  {s.line2}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pause / play toggle — drives whichever motion layer is topmost. */}
      <button
        type="button"
        onClick={togglePlay}
        aria-label={playing ? "Pause background animation" : "Play background animation"}
        data-testid="hero-video-toggle"
        className="group absolute bottom-5 end-5 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/30 bg-black/20 text-white opacity-60 backdrop-blur-sm transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white motion-reduce:hidden"
      >
        {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
      </button>
    </section>
  );
}
